import { resolveFormTenantId } from "../../utils/tenantContext";
import React, { useState, useEffect } from "react";
import { getLocalSystemDate, parseIsoDateToMDYUS, toIsoDate } from '../../utils/dateUtils';
import { mergeFormDataWithStatusExtras, applyIsoDatesToFields } from '../../utils/applicationFormPayload';
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "react-hot-toast";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import EvaluationInput from "../../pages/EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../../pages/EvaluationNew/components/InputDatePicker";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import ApplicationFormFooter from "./ApplicationFormFooter";
import { request } from "../../utils/api";
import { useApplicationFormLogic } from "../../hooks/useApplicationFormLogic";
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";

const administrativeOrientationSchema = z.object({
  tenantId: z.any().optional(),
  userId: z.any().optional(),
  applicantSignature: z.string().min(1, "Applicant signature is required"),
  applicantName: z.string().min(1, "Applicant name is required"),
  applicantSignedAt: z.string().min(1, "Applicant signed at is required"),
  position: z.string().optional(),
  applicantStatementAccepted: z.literal(true, {
    errorMap: () => ({ message: "You must certify that you have read and accepted the terms." })
  }),
  items: z.array(z.object({
    section: z.string().min(1, "Section is required"),
    itemName: z.string().min(1, "Item name is required"),
    isChecked: z.literal(true, {
      errorMap: () => ({ message: "Must check all items" })
    }),
    employeeInitials: z.string().min(1, "Initials required"),
    dateDiscussed: z.string().min(1, "Date required")
  })),
  reviewedBySignature: z.string().optional(),
  reviewedBySignedAt: z.string().optional(),
  reviewedBySignedBy: z.string().optional()
});

export const ORIENTATION_ITEMS = [
  // Agency Policies and Procedures
  ...[
    "Organizational Chart",
    "Agency Overview",
    "Philosophy of Care",
    "Employee Handbook",
    "Drug-Free Workplace",
    "Attendance/Punctuality",
    "Dress Code/Identification",
    "Probationary Period",
    "Performance Evaluation",
    "Disciplinary Action",
    "Grievance Procedure",
    "Patient Rights",
    "Advanced Directives",
    "Abuse and Neglect",
    "Security/Violence in Workplace",
  ].map((item) => ({ section: "Agency Policies and Procedures", itemName: item })),

  // Forms
  ...[
    "CHHA Aide Activity Log",
    "CHHA Aide Activity Record",
    "CHHA Aide Activity Report",
    "CHHA Aide Activity Summary",
    "CHHA Aide Activity Documentation",
    "CHHA Aide Activity Log 2",
    "CHHA Aide Activity Record 2",
    "CHHA Aide Activity Report 2",
    "CHHA Aide Activity Summary 2",
    "CHHA Aide Activity Documentation 2",
    "Infection Control Report",
  ].map((item) => ({ section: "Forms", itemName: item })),

  // Infection Control Orientation
  ...[
    "Attend Lecture",
    "Tuberculosis",
    "Universal Precautions",
    "Demonstrated Hand Washing",
    "Sharps Container",
    "Review PPE (Personal Protective Equipment)",
    "Receive PPE",
    "Post Test",
  ].map((item) => ({
    section: "Infection Control Orientation",
    itemName: item,
  })),
];

function AdministrativeOrientation(props) {
  const { positionName, onClose = () => { } } = props;
  const [orientationId, setOrientationId] = useState(null);

  const methods = useForm({
    resolver: zodResolver(administrativeOrientationSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      applicantSignature: "",
      applicantName: "",
      applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
      position: positionName || "",
      applicantStatementAccepted: false,
      items: ORIENTATION_ITEMS.map(item => ({
        ...item,
        isChecked: false,
        employeeInitials: "",
        dateDiscussed: parseIsoDateToMDYUS(getLocalSystemDate())
      })),
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: ""
    },
    mode: "onChange"
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
  } = methods;

  const [currentStatus, setCurrentStatus] = useState("pending");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const merged = mergeFormDataWithStatusExtras(data, additionalData);
      const payload = {
        ...merged,
        ...applyIsoDatesToFields(merged, ["applicantSignedAt", "reviewedBySignedAt"]),
        items: (merged.items || []).map((item) => ({
          ...item,
          dateDiscussed: toIsoDate(item.dateDiscussed),
        })),
        flag: actualFlag,
      };

      // Ensure applicantName is present in payload
      if (!payload.applicantName && merged.applicantName) {
        payload.applicantName = merged.applicantName;
      }

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      const method = orientationId ? 'PUT' : 'POST';
      const url = orientationId ? `/administrative-orientations/${orientationId}` : '/administrative-orientations';

      const response = await request(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.status === 1) {
        toast.success(response.message || "Administrative Orientation processed successfully!");
        closeForm()
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(`Error: ${error.message} `);
    }
  };

  const {
    handleStatusAction,
    handleSave,
    targetUserId,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,
    actualFromId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "applicantName",
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    adminSignatureField: "reviewedBySignature",
    adminNameField: "reviewedBySignedBy",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });



  // Actually, useApplicationFormLogic returns all these. I should destructure them above.

  const { fields } = useFieldArray({
    control,
    name: "items"
  });

  useEffect(() => {
    if (positionName) {
      setValue("position", positionName);
    }
  }, [positionName, setValue]);

  useEffect(() => {
    const fetchAdministrativeOrientation = async () => {
      // Must wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId) return;

      try {
        if (!actualFromId && !targetUserId && !orientationId) {
          if (currentUserData && !isAdmin) {
            methods.reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              applicantName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''} `.trim(),
              applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
              position: positionName || "",
              items: ORIENTATION_ITEMS.map(item => ({
                ...item,
                isChecked: false,
                employeeInitials: "",
                dateDiscussed: parseIsoDateToMDYUS(getLocalSystemDate())
              }))
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/administrative-orientations/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/administrative-orientations?userId=${targetUserId}`);
          }
        } else {
          response = await request(`/administrative-orientations/${actualFromId || targetUserId}`);
        }

        let data = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) data = response.result[0];
          } else if (response.result?.orientation) {
            data = response.result.orientation;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            // Logic from reference: find item by userId
            data = response.result.items.find(item => item.userId === Number.parseInt(targetUserId));
          }
        }

        if (data) {
          setOrientationId(data.id);
          setCurrentStatus(data.status || "");
          // setComments(data.comments || ""); // Add comments state if missing

          const formattedItems = (data.items || []).map(item => ({
            ...item,
            dateDiscussed: item.dateDiscussed ? item.dateDiscussed.split('T')[0] : ""
          }));

          const mergedItems = ORIENTATION_ITEMS.map(defaultItem => {
            const existing = formattedItems.find(i => i.itemName === defaultItem.itemName && i.section === defaultItem.section);
            return existing ? { ...defaultItem, ...existing } : {
              ...defaultItem,
              isChecked: false,
              employeeInitials: "",
              dateDiscussed: ""
            };
          });

          methods.reset({
            ...data,
            applicantName: data.applicantName || data.applicantSignedBy || "",
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).applicantSignedAt || getLocalSystemDate()),
            reviewedBySignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).reviewedBySignedAt || ""),
            items: mergedItems
          });
        }
      } catch (error) {
        console.error("Error fetching orientation:", error);
      }
    };
    fetchAdministrativeOrientation();
  }, [targetUserId, reset, currentUserData, isAdmin, methods, actualFromId, orientationId, positionName]);



  const sections = Array.from(new Set(ORIENTATION_ITEMS.map(item => item.section)));

  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <EvaluationFormHeader title="Administrative Orientation Checklist" />

        <div className="p-8">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <CorrectionRequestAlert status={currentStatus} comments={methods.watch("comments")} />
              <fieldset disabled={isReadOnly} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <EvaluationInput
                    name="applicantName"
                    label="Name of Applicant:"
                    placeholder="Enter full name"
                  />
                  <EvaluationInput
                    name="position"
                    label="Position Applied For:"
                    readOnly
                  />
                </div>

                <div className="space-y-12">
                  {sections.map((section) => (
                    <div key={section} className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                      <div className="bg-gray-50 border-b border-gray-200 p-4">
                        <h3 className="text-lg font-bold text-gray-800 uppercase tracking-wide">
                          {section}
                        </h3>
                      </div>
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-white text-gray-700">
                            <th className="p-4 text-left font-bold uppercase tracking-wider w-24">Items</th>
                            <th className="p-4 text-center font-bold uppercase tracking-wider w-10">Check <span className="text-red-500">*</span></th>
                            <th className="p-4 text-center font-bold uppercase tracking-wider w-32">Initials <span className="text-red-500">*</span></th>
                            <th className="p-4 text-center font-bold uppercase tracking-wider w-40">Date <span className="text-red-500">*</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          {fields.map((field, index) => {
                            if (field.section !== section) return null;
                            return (
                              <tr key={field.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                                <td className="p-4 text-gray-800 font-medium">{field.itemName}</td>
                                <td className="p-4 text-center">
                                  <div className="flex flex-col items-center">
                                    <input
                                      type="checkbox"
                                      {...methods.register(`items.${index}.isChecked`)}
                                      className={`h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer ${methods.formState.errors?.items?.[index]?.isChecked ? "border-red-500 ring-1 ring-red-500" : ""
                                        }`}
                                    />
                                    {methods.formState.errors?.items?.[index]?.isChecked && (
                                      <p className="text-red-500 text-[10px] mt-1 font-medium leading-tight">
                                        Required
                                      </p>
                                    )}
                                  </div>
                                </td>
                                <td className="p-4">
                                  <EvaluationInput
                                    name={`items.${index}.employeeInitials`}
                                    placeholder="Initials"
                                    className="text-center"
                                    isRequired
                                  />
                                </td>
                                <td className="p-4">
                                  <InputDatePicker
                                    formatSeparator="/"
                                    name={`items.${index}.dateDiscussed`}
                                    max={getLocalSystemDate()}
                                    isRequired
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </fieldset>

              <ApplicationFormFooter
                page={1}
                totalPages={1}
                setPage={() => { }}
                methods={methods}
                onSave={handleSave}
                onSubmit={(data, status) => onSubmit(data, status)}
                onStatusUpdate={handleStatusAction}
                isSignatureModalOpen={isSignatureModalOpen}
                openSignatureModal={openSignatureModal}
                closeSignatureModal={closeSignatureModal}
                handleSignatureApply={handleSignatureApply}
                signerName={signerName}
                applicantName={methods.watch("applicantName")}
                applicantSignature={methods.watch("applicantSignature")}
                applicantSignedAt={methods.watch("applicantSignedAt")}
                reviewedBySignature={methods.watch("reviewedBySignature")}
                reviewedBySignedAt={methods.watch("reviewedBySignedAt")}
                reviewedBySignedBy={methods.watch("reviewedBySignedBy")}
                isReadOnly={isReadOnly}
                onClose={closeForm}
                currentStatus={currentStatus}
                submitLabel="Submit"
              />
            </form>
          </FormProvider>
        </div>
      </div>
    </>
  );
}

export default AdministrativeOrientation;
